<script>
import { defineAsyncComponent, useTemplateRef } from 'vue';
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useTrack } from 'dashboard/composables';
import keyboardEventListenerMixins from 'shared/mixins/keyboardEventListenerMixins';

import ReplyToMessage from './ReplyToMessage.vue';
import SpellCheckerSuggestions from './SpellCheckerSuggestions.vue';
import CannedResponse from './CannedResponse.vue';
import ResizableTextArea from 'shared/components/ResizableTextArea.vue';
import AttachmentPreview from 'dashboard/components/widgets/AttachmentsPreview.vue';
import ReplyTopPanel from 'dashboard/components/widgets/WootWriter/ReplyTopPanel.vue';
import ReplyEmailHead from './ReplyEmailHead.vue';
import ReplyBottomPanel from 'dashboard/components/widgets/WootWriter/ReplyBottomPanel.vue';
import CopilotReplyBottomPanel from 'dashboard/components/widgets/WootWriter/CopilotReplyBottomPanel.vue';
import ArticleSearchPopover from 'dashboard/routes/dashboard/helpcenter/components/ArticleSearch/SearchPopover.vue';
import CopilotEditorSection from './CopilotEditorSection.vue';
import MessageSignatureMissingAlert from './MessageSignatureMissingAlert.vue';
import ReplyBoxBanner from './ReplyBoxBanner.vue';
import QuotedEmailPreview from './QuotedEmailPreview.vue';
import { REPLY_EDITOR_MODES } from 'dashboard/components/widgets/WootWriter/constants';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import AudioRecorder from 'dashboard/components/widgets/WootWriter/AudioRecorder.vue';
import { AUDIO_FORMATS } from 'shared/constants/messages';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { CMD_AI_ASSIST } from 'dashboard/helper/commandbar/events';
import {
  getMessageVariables,
  getUndefinedVariablesInMessage,
} from '@chatwoot/utils';
import QuepasaAPI from 'dashboard/api/quepasa';
import BrasilApiModal from './BrasilApiModal.vue';
import WhatsappTemplates from './WhatsappTemplates/Modal.vue';
import ContentTemplates from './ContentTemplates/ContentTemplatesModal.vue';
import { MESSAGE_MAX_LENGTH } from 'shared/helpers/MessageTypeHelper';
import inboxMixin, { INBOX_FEATURES } from 'shared/mixins/inboxMixin';
import {
  trimContent,
  debounce,
  getRecipients,
  replaceVariablesInMessage,
} from '@chatwoot/utils';
import wootConstants from 'dashboard/constants/globals';
import {
  extractQuotedEmailText,
  buildQuotedEmailHeader,
  truncatePreviewText,
  appendQuotedTextToMessage,
} from 'dashboard/helper/quotedEmailHelper';
import {
  CONVERSATION_EVENTS,
  CAPTAIN_EVENTS,
} from '../../../helper/AnalyticsHelper/events';
import fileUploadMixin from 'dashboard/mixins/fileUploadMixin';
import {
  appendSignature,
  removeSignature,
  replaceSignature,
  getEffectiveChannelType,
  extractTextFromMarkdown,
} from 'dashboard/helper/editorHelper';
import { useCopilotReply } from 'dashboard/composables/useCopilotReply';
import { useKbd } from 'dashboard/composables/utils/useKbd';
import { isFileTypeAllowedForChannel } from 'shared/helpers/FileHelper';

import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { LocalStorage } from 'shared/helpers/localStorage';
import { emitter } from 'shared/helpers/mitt';
const EmojiInput = defineAsyncComponent(
  () => import('shared/components/emoji/EmojiInput.vue')
);

export default {
  components: {
    ArticleSearchPopover,
    AttachmentPreview,
    AudioRecorder,
    CannedResponse,
    ReplyBoxBanner,
    EmojiInput,
    SpellCheckerSuggestions,
    MessageSignatureMissingAlert,
    ReplyBottomPanel,
    ReplyEmailHead,
    ResizableTextArea,
    ReplyToMessage,
    ReplyTopPanel,
    BrasilApiModal,
    ContentTemplates,
    WhatsappTemplates,
    WootMessageEditor,
    QuotedEmailPreview,
    CopilotEditorSection,
    CopilotReplyBottomPanel,
  },
  mixins: [inboxMixin, fileUploadMixin, keyboardEventListenerMixins],
  emits: ['toggleEditorSize'],
  setup() {
    const {
      uiSettings,
      isEditorHotKeyEnabled,
      updateUISettings,
      fetchSignatureFlagFromUISettings,
      setQuotedReplyFlagForInbox,
      fetchQuotedReplyFlagFromUISettings,
    } = useUISettings();

    const replyEditor = useTemplateRef('replyEditor');
    const messageEditor = useTemplateRef('messageEditor');
    const copilot = useCopilotReply();
    const shortcutKey = useKbd(['$mod', '+', 'enter']);

    return {
      uiSettings,
      isEditorHotKeyEnabled,
      updateUISettings,
      fetchSignatureFlagFromUISettings,
      setQuotedReplyFlagForInbox,
      fetchQuotedReplyFlagFromUISettings,
      replyEditor,
      messageEditor,
      copilot,
      shortcutKey,
    };
  },
  data() {
    return {
      message: '',
      inReplyTo: {},
      isFocused: false,
      showEmojiPicker: false,
      attachedFiles: [],
      isRecordingAudio: false,
      recordingAudioState: '',
      recordingAudioDurationText: '',
      replyType: REPLY_EDITOR_MODES.REPLY,
      bccEmails: '',
      ccEmails: '',
      toEmails: '',
      doAutoSaveDraft: () => {},
      showBrasilApiModal: false,
      showWhatsAppTemplatesModal: false,
      showContentTemplatesModal: false,
      updateEditorSelectionWith: '',
      undefinedVariableMessage: '',
      mentionSearchKey: '',
      hasSlashCommand: false,
      showMentions: false,
      showUserMentions: false,
      showCannedMenu: false,
      showVariablesMenu: false,
      newConversationModalActive: false,
      showArticleSearchPopover: false,
      hasRecordedAudio: false,
      pixCode: '',
      showPixTextModal: false,
      directMessage: '',
      directSending: false,
      directRateLimit: {},
      directFile: null,
      directCannedPhotos: [],
      directRecordingAudio: false,
      directAudioState: 'idle',
      directAudioDuration: '',
      quepasaConnected: null,
      pendingDirectResend: false,
      pixStandardText: `Copie todo o código, abra o App do seu banco, selecione o PIX e cole o código.
Não pode ter espaço. Inicia em 0002010 e termina em 6304BD29
Ou pode pagar pelo QR code da imagem a seguir também.
✅ O pagamento deve ser realizado em até 30 minutos após a geração do código Pix.
✅ Caso o pagamento não seja efetuado nesse prazo, a reserva será automaticamente cancelada.`,
      copilotAcceptedMessages: {},
      messagingWindowTimer: null,
    };
  },
  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      messageSignature: 'getMessageSignature',
      currentUser: 'getCurrentUser',
      lastEmail: 'getLastEmailInSelectedChat',
      globalConfig: 'globalConfig/get',
    }),
    currentContact() {
      const senderId = this.currentChat?.meta?.sender?.id;
      if (!senderId) return {};
      return this.$store.getters['contacts/getContact'](senderId);
    },
    isRichEditorEnabled() {
      return this.isAWebWidgetInbox || this.isAnEmailChannel;
    },
    showRichContentEditor() {
      if (this.isOnPrivateNote || this.isRichEditorEnabled) {
        return true;
      }
      if (this.isAPIInbox) {
        const {
          display_rich_content_editor: displayRichContentEditor = false,
        } = this.uiSettings;
        return displayRichContentEditor;
      }
      return false;
    },
    shouldShowReplyToMessage() {
      return (
        this.inReplyTo?.id &&
        !this.isPrivate &&
        this.inboxHasFeature(INBOX_FEATURES.REPLY_TO) &&
        !this.is360DialogWhatsAppChannel &&
        !this.copilot.isActive.value
      );
    },
    showWhatsappTemplates() {
      // We support templates for API channels if someone updates templates manually via API
      // That's why we don't explicitly check for channel type here
      const templates = this.$store.getters['inboxes/getWhatsAppTemplates'](
        this.inboxId
      );
      return !!(templates && templates.length) && !this.isPrivate;
    },
    showContentTemplates() {
      return this.isATwilioWhatsAppChannel && !this.isPrivate;
    },
    isPrivate() {
      if (
        this.currentChat.can_reply ||
        this.isAWhatsAppChannel ||
        this.isAPIInbox
      ) {
        return this.isOnPrivateNote;
      }
      return true;
    },
    isReplyRestricted() {
      return (
        !this.currentChat?.can_reply &&
        !(this.isAWhatsAppChannel || this.isAPIInbox)
      );
    },
    inboxId() {
      return this.currentChat.inbox_id;
    },
    inbox() {
      return this.$store.getters['inboxes/getInbox'](this.inboxId);
    },
    messagePlaceHolder() {
      if (this.isEditorDisabled) {
        if (this.isAWhatsAppChannel) {
          return this.$t('CONVERSATION.FOOTER.MESSAGING_RESTRICTED_WHATSAPP');
        }
        if (this.isAPIInbox) {
          return this.$t('CONVERSATION.FOOTER.MESSAGING_RESTRICTED_API');
        }
        return this.$t('CONVERSATION.FOOTER.MESSAGING_RESTRICTED');
      }
      return this.isPrivate
        ? this.$t('CONVERSATION.FOOTER.PRIVATE_MSG_INPUT')
        : this.$t('CONVERSATION.FOOTER.MSG_INPUT');
    },
    isMessageLengthReachingThreshold() {
      return this.message.length > this.maxLength - 50;
    },
    charactersRemaining() {
      return this.maxLength - this.message.length;
    },
    isReplyButtonDisabled() {
      if (this.isEditorDisabled) return true;
      if (this.isATwitterInbox) return true;
      if (this.hasAttachments || this.hasRecordedAudio) return false;

      return (
        this.isMessageEmpty ||
        this.message.length === 0 ||
        this.message.length > this.maxLength
      );
    },
    sender() {
      return {
        name: this.currentUser.name,
        thumbnail: this.currentUser.avatar_url,
      };
    },
    conversationType() {
      const { additional_attributes: additionalAttributes } = this.currentChat;
      const type = additionalAttributes ? additionalAttributes.type : '';
      return type || '';
    },
    maxLength() {
      if (this.isPrivate) {
        return MESSAGE_MAX_LENGTH.GENERAL;
      }
      if (this.isAFacebookInbox) {
        return MESSAGE_MAX_LENGTH.FACEBOOK;
      }
      if (this.isAnInstagramChannel) {
        return MESSAGE_MAX_LENGTH.INSTAGRAM;
      }
      if (this.isATelegramChannel) {
        return MESSAGE_MAX_LENGTH.TELEGRAM;
      }
      if (this.isATiktokChannel) {
        return MESSAGE_MAX_LENGTH.TIKTOK;
      }
      if (this.isATwilioWhatsAppChannel) {
        return MESSAGE_MAX_LENGTH.TWILIO_WHATSAPP;
      }
      if (this.isAWhatsAppCloudChannel) {
        return MESSAGE_MAX_LENGTH.WHATSAPP_CLOUD;
      }
      if (this.isASmsInbox) {
        return MESSAGE_MAX_LENGTH.TWILIO_SMS;
      }
      if (this.isAnEmailChannel) {
        return MESSAGE_MAX_LENGTH.EMAIL;
      }
      if (this.isATwilioSMSChannel) {
        return MESSAGE_MAX_LENGTH.TWILIO_SMS;
      }
      if (this.isAWhatsAppChannel) {
        return MESSAGE_MAX_LENGTH.WHATSAPP_CLOUD;
      }
      return MESSAGE_MAX_LENGTH.GENERAL;
    },
    showFileUpload() {
      const { image_send: imageSend } =
        this.currentChat?.additional_attributes?.tiktok_capabilities ?? {};
      const tiktokAttachmentSupported = imageSend ?? true;

      return (
        this.isAWebWidgetInbox ||
        this.isAFacebookInbox ||
        this.isAWhatsAppChannel ||
        this.isAPIInbox ||
        this.isAnEmailChannel ||
        this.isASmsInbox ||
        this.isATelegramChannel ||
        this.isALineChannel ||
        this.isAnInstagramChannel ||
        (this.isATiktokChannel && tiktokAttachmentSupported)
      );
    },
    replyButtonLabel() {
      let sendMessageText = this.$t('CONVERSATION.REPLYBOX.SEND');
      if (this.isPrivate) {
        sendMessageText = this.$t('CONVERSATION.REPLYBOX.CREATE');
      }
      const keyLabel = this.isEditorHotKeyEnabled('cmd_enter')
        ? `(${this.shortcutKey})`
        : '(↵)';
      return `${sendMessageText} ${keyLabel}`;
    },
    replyBoxClass() {
      return {
        'is-private': this.isPrivate,
        'is-focused': this.isFocused || this.hasAttachments,
      };
    },
    hasAttachments() {
      return this.attachedFiles.length;
    },
    showAudioRecorder() {
      return !this.isOnPrivateNote && this.showFileUpload;
    },
    showAudioRecorderEditor() {
      return this.showAudioRecorder && this.isRecordingAudio;
    },
    isOnPrivateNote() {
      return this.replyType === REPLY_EDITOR_MODES.NOTE;
    },
    isOnPixMode() {
      return this.replyType === REPLY_EDITOR_MODES.PIX;
    },
    isOnDirectMode() {
      return this.replyType === REPLY_EDITOR_MODES.DIRECT;
    },
    showDirectSendTab() {
      return true;
    },
    hasQuepasaConfig() {
      return !!(this.inbox.quepasa_token);
    },
    contactPhoneForQuepasa() {
      const phone = this.currentContact?.phone_number || '';
      return phone.replace(/\D/g, '');
    },
    isOnExpandedLayout() {
      const {
        LAYOUT_TYPES: { CONDENSED },
      } = wootConstants;
      const { conversation_display_type: conversationDisplayType = CONDENSED } =
        this.uiSettings;
      return conversationDisplayType !== CONDENSED;
    },
    isMessageEmpty() {
      if (!this.message) {
        return true;
      }
      return !this.message.trim().replace(/\n/g, '').length;
    },
    showReplyHead() {
      return !this.isOnPrivateNote && this.isAnEmailChannel;
    },
    enableMultipleFileUpload() {
      return (
        this.isAnEmailChannel ||
        this.isAWebWidgetInbox ||
        this.isAPIInbox ||
        this.isAWhatsAppChannel ||
        this.isATelegramChannel
      );
    },
    isSignatureEnabledForInbox() {
      return !this.isPrivate && this.sendWithSignature;
    },
    isSignatureAvailable() {
      return !!this.messageSignature;
    },
    sendWithSignature() {
      return this.fetchSignatureFlagFromUISettings(this.channelType);
    },
    signatureToApply() {
      return this.showRichContentEditor
        ? this.messageSignature
        : extractTextFromMarkdown(this.messageSignature);
    },
    conversationId() {
      return this.currentChat.id;
    },
    conversationIdByRoute() {
      return this.conversationId;
    },
    editorStateId() {
      return `draft-${this.conversationIdByRoute}-${this.replyType}`;
    },
    audioRecordFormat() {
      if (this.isAWhatsAppChannel || this.isATelegramChannel) {
        return AUDIO_FORMATS.MP3;
      }
      if (this.isAPIInbox) {
        return AUDIO_FORMATS.MP3;
      }
      return AUDIO_FORMATS.WAV;
    },
    messageVariables() {
      const variables = getMessageVariables({
        conversation: this.currentChat,
        contact: this.currentContact,
        inbox: this.inbox,
      });
      return variables;
    },
    connectedPortalSlug() {
      const { help_center: portal = {} } = this.inbox;
      const { slug = '' } = portal;
      return slug;
    },
    quotedReplyPreference() {
      if (!this.isAnEmailChannel) {
        return false;
      }

      return !!this.fetchQuotedReplyFlagFromUISettings(this.channelType);
    },
    lastEmailWithQuotedContent() {
      if (!this.isAnEmailChannel) {
        return null;
      }

      const lastEmail = this.lastEmail;
      if (!lastEmail || lastEmail.private) {
        return null;
      }

      return lastEmail;
    },
    quotedEmailText() {
      return extractQuotedEmailText(this.lastEmailWithQuotedContent);
    },
    quotedEmailPreviewText() {
      return truncatePreviewText(this.quotedEmailText, 80);
    },
    shouldShowQuotedReplyToggle() {
      return this.isAnEmailChannel && !this.isOnPrivateNote;
    },
    shouldShowQuotedPreview() {
      return (
        this.shouldShowQuotedReplyToggle &&
        this.quotedReplyPreference &&
        !!this.quotedEmailText
      );
    },
    isDefaultEditorMode() {
      return !this.showAudioRecorderEditor && !this.copilot.isActive.value;
    },
    isEditorDisabled() {
      return (
        (this.isAWhatsAppChannel || this.isAPIInbox) &&
        !this.isOnPrivateNote &&
        !this.currentChat.can_reply
      );
    },
  },
  watch: {
    currentChat(conversation, oldConversation) {
      const { can_reply: canReply } = conversation;
      if (oldConversation && oldConversation.id !== conversation.id) {
        // Only update email fields when switching to a completely different conversation (by ID)
        // This prevents overwriting user input (e.g., CC/BCC fields) when performing actions
        // like self-assign or other updates that do not actually change the conversation context
        this.setCCAndToEmailsFromLastChat();
        // Reset Copilot editor state (includes cancelling ongoing generation)
        this.copilot.reset();
      }

      // Reconfigure messaging window timer when conversation changes or
      // when can_reply/expires_at is updated (e.g. new incoming message)
      if (
        !oldConversation ||
        oldConversation.id !== conversation.id ||
        oldConversation.can_reply !== conversation.can_reply ||
        oldConversation.messaging_window_expires_at !==
          conversation.messaging_window_expires_at
      ) {
        this.setupMessagingWindowTimer();
      }

      if (this.isOnPrivateNote) {
        return;
      }

      if (canReply || this.isAWhatsAppChannel || this.isAPIInbox) {
        this.replyType = REPLY_EDITOR_MODES.REPLY;
      } else {
        this.replyType = REPLY_EDITOR_MODES.NOTE;
      }

      this.fetchAndSetReplyTo();
    },
    // When moving from one conversation to another, the store may not have the
    // list of all the messages. A fetch is subsequently made to get the messages.
    // This watcher handles two main cases:
    // 1. When switching conversations and messages are fetched/updated, ensures CC/BCC fields are set from the latest OUTGOING/INCOMING email (not activity/private messages).
    // 2. Fixes and issue where CC/BCC fields could be reset/lost after assignment/activity actions or message mutations that did not represent a true email context change.
    lastEmail: {
      handler(lastEmail) {
        if (!lastEmail) return;
        this.setCCAndToEmailsFromLastChat();
      },
      deep: true,
    },
    conversationIdByRoute(conversationId, oldConversationId) {
      if (conversationId !== oldConversationId) {
        this.setToDraft(oldConversationId, this.replyType);
        this.getFromDraft();
        this.resetRecorderAndClearAttachments();
      }
    },
    message(updatedMessage) {
      const bodyWithoutSignature = removeSignature(
        updatedMessage,
        this.signatureToApply
      );
      const startsWithSlash = bodyWithoutSignature.startsWith('/');
      this.hasSlashCommand = startsWithSlash && !this.showRichContentEditor;
      this.showMentions = this.hasSlashCommand;
      this.mentionSearchKey = this.hasSlashCommand
        ? bodyWithoutSignature.substring(1)
        : '';
      this.doAutoSaveDraft();
    },
    replyType(updatedReplyType, oldReplyType) {
      this.setToDraft(this.conversationIdByRoute, oldReplyType);
      this.getFromDraft();
    },
  },

  mounted() {
    this.setupMessagingWindowTimer();
    this.getFromDraft();
    this.loadPixStandardText();
    // Don't use the keyboard listener mixin here as the events here are supposed to be
    // working even if the editor is focussed.
    document.addEventListener('paste', this.onPaste);
    this.setCCAndToEmailsFromLastChat();
    this.doAutoSaveDraft = debounce(
      () => {
        this.saveDraft(this.conversationIdByRoute, this.replyType);
      },
      500,
      true
    );

    this.fetchAndSetReplyTo();
    emitter.on(BUS_EVENTS.TOGGLE_REPLY_TO_MESSAGE, this.onReplyToMessage);

    // A hacky fix to solve the drag and drop
    // Is showing on top of new conversation modal drag and drop
    // TODO need to find a better solution
    emitter.on(
      BUS_EVENTS.NEW_CONVERSATION_MODAL,
      this.onNewConversationModalActive
    );
    emitter.on(BUS_EVENTS.INSERT_INTO_NORMAL_EDITOR, this.addIntoEditor);
    emitter.on(CMD_AI_ASSIST, this.executeCopilotAction);
    emitter.on(BUS_EVENTS.QUEPASA_RECONNECTED, this.onQuepasaReconnected);
  },
  unmounted() {
    this.clearMessagingWindowTimer();
    document.removeEventListener('paste', this.onPaste);
    document.removeEventListener('keydown', this.handleKeyEvents);
    emitter.off(BUS_EVENTS.TOGGLE_REPLY_TO_MESSAGE, this.onReplyToMessage);
    emitter.off(BUS_EVENTS.INSERT_INTO_NORMAL_EDITOR, this.addIntoEditor);
    emitter.off(
      BUS_EVENTS.NEW_CONVERSATION_MODAL,
      this.onNewConversationModalActive
    );
    emitter.off(CMD_AI_ASSIST, this.executeCopilotAction);
    emitter.off(BUS_EVENTS.QUEPASA_RECONNECTED, this.onQuepasaReconnected);
  },
  methods: {
    setupMessagingWindowTimer() {
      this.clearMessagingWindowTimer();
      const expiresAt = this.currentChat?.messaging_window_expires_at;
      if (!expiresAt || !this.currentChat?.can_reply) return;

      const now = Math.floor(Date.now() / 1000);
      const delay = (expiresAt - now) * 1000;
      if (delay <= 0) return;

      this.messagingWindowTimer = setTimeout(() => {
        this.$store.commit('SET_CONVERSATION_CAN_REPLY', {
          conversationId: this.currentChat.id,
          canReply: false,
        });
      }, delay);
    },
    clearMessagingWindowTimer() {
      if (this.messagingWindowTimer) {
        clearTimeout(this.messagingWindowTimer);
        this.messagingWindowTimer = null;
      }
    },
    getDraftKey(
      conversationId = this.conversationIdByRoute,
      replyType = this.replyType
    ) {
      return `draft-${conversationId}-${replyType}`;
    },
    getCopilotAcceptedMessage(replyType = this.replyType) {
      const key = this.getDraftKey(this.conversationIdByRoute, replyType);
      return this.copilotAcceptedMessages[key] || '';
    },
    setCopilotAcceptedMessage(message, replyType = this.replyType) {
      const key = this.getDraftKey(this.conversationIdByRoute, replyType);
      this.copilotAcceptedMessages[key] = trimContent(
        message || '',
        this.maxLength
      );
    },
    clearCopilotAcceptedMessage(replyType = this.replyType) {
      const key = this.getDraftKey(this.conversationIdByRoute, replyType);
      delete this.copilotAcceptedMessages[key];
    },
    handleInsert(article) {
      const { url, title } = article;
      // Removing empty lines from the title
      const lines = title.split('\n');
      const nonEmptyLines = lines.filter(line => line.trim() !== '');
      const filteredMarkdown = nonEmptyLines.join(' ');
      emitter.emit(
        BUS_EVENTS.INSERT_INTO_RICH_EDITOR,
        `[${filteredMarkdown}](${url})`
      );

      useTrack(CONVERSATION_EVENTS.INSERT_ARTICLE_LINK);
    },
    toggleQuotedReply() {
      if (!this.isAnEmailChannel) {
        return;
      }

      const nextValue = !this.quotedReplyPreference;
      this.setQuotedReplyFlagForInbox(this.channelType, nextValue);
    },
    shouldIncludeQuotedEmail() {
      return (
        this.quotedReplyPreference &&
        this.shouldShowQuotedReplyToggle &&
        !!this.quotedEmailText
      );
    },
    getMessageWithQuotedEmailText(message) {
      if (!this.shouldIncludeQuotedEmail()) {
        return message;
      }

      const quotedText = this.quotedEmailText || '';
      const header = buildQuotedEmailHeader(
        this.lastEmailWithQuotedContent,
        this.currentContact,
        this.inbox
      );

      return appendQuotedTextToMessage(message, quotedText, header);
    },
    resetRecorderAndClearAttachments() {
      // Reset audio recorder UI state
      this.resetAudioRecorderInput();
      // Reset attached files
      this.attachedFiles = [];
    },
    saveDraft(conversationId, replyType) {
      if (this.message || this.message === '') {
        const key = this.getDraftKey(conversationId, replyType);
        const draftToSave = trimContent(this.message || '', this.maxLength);

        this.$store.dispatch('draftMessages/set', {
          key,
          message: draftToSave,
        });
      }
    },
    setToDraft(conversationId, replyType) {
      this.saveDraft(conversationId, replyType);
      this.message = '';
    },
    getFromDraft() {
      if (this.conversationIdByRoute) {
        const key = this.getDraftKey();
        const messageFromStore =
          this.$store.getters['draftMessages/get'](key) || '';

        // ensure that the message has signature set based on the ui setting
        this.message = this.toggleSignatureForDraft(messageFromStore);
      }
    },
    toggleSignatureForDraft(message) {
      if (this.isPrivate) {
        return message;
      }

      // Even when editor is disabled (e.g. WhatsApp/API can't reply), we must
      // still normalize stale signatures out of drafts when signature is off.
      if (this.isEditorDisabled && this.sendWithSignature) {
        return message;
      }

      const effectiveChannelType = getEffectiveChannelType(
        this.channelType,
        this.inbox?.medium || ''
      );

      return this.sendWithSignature
        ? appendSignature(message, this.messageSignature, effectiveChannelType)
        : removeSignature(message, this.messageSignature, effectiveChannelType);
    },
    removeFromDraft() {
      if (this.conversationIdByRoute) {
        const key = this.getDraftKey();
        this.$store.dispatch('draftMessages/delete', { key });
      }
    },
    getElementToBind() {
      return this.replyEditor;
    },
    getKeyboardEvents() {
      return {
        Escape: {
          action: () => {
            this.hideEmojiPicker();
          },
          allowOnFocusedInput: true,
        },
        '$mod+KeyK': {
          action: e => {
            e.preventDefault();
            const ninja = document.querySelector('ninja-keys');
            ninja.open();
          },
          allowOnFocusedInput: true,
        },
        Enter: {
          action: e => {
            if (this.isAValidEvent('enter')) {
              this.onSendReply();
              e.preventDefault();
            }
          },
          allowOnFocusedInput: true,
        },
        '$mod+Enter': {
          action: () => {
            if (this.copilot.isActive.value && this.isFocused) {
              this.onSubmitCopilotReply();
            } else if (this.isAValidEvent('cmd_enter')) {
              this.onSendReply();
            }
          },
          allowOnFocusedInput: true,
        },
      };
    },
    isAValidEvent(selectedKey) {
      return (
        !this.showUserMentions &&
        !this.showMentions &&
        !this.showCannedMenu &&
        !this.showVariablesMenu &&
        this.isFocused &&
        this.isEditorHotKeyEnabled(selectedKey)
      );
    },
    onPaste(e) {
      // Don't handle paste if compose new conversation modal is open
      if (this.newConversationModalActive) return;

      // Don't handle paste if editor is disabled
      if (this.isEditorDisabled) return;
      if (!this.showFileUpload && !this.isOnPrivateNote) return;

      // Filter valid files (non-zero size)
      Array.from(e.clipboardData.files)
        .filter(file => file.size > 0)
        .filter(file => {
          const isAllowed = isFileTypeAllowedForChannel(file, {
            channelType: this.channelType || this.inbox?.channel_type,
            medium: this.inbox?.medium,
            conversationType: this.conversationType,
            isInstagramChannel: this.isAnInstagramChannel,
            isOnPrivateNote: this.isOnPrivateNote,
          });

          if (!isAllowed) {
            useAlert(
              this.$t('CONVERSATION.FILE_TYPE_NOT_SUPPORTED', {
                fileName: file.name,
              })
            );
          }

          return isAllowed;
        })
        .forEach(file => {
          const { name, type, size } = file;
          this.onFileUpload({ name, type, size, file });
        });
    },
    toggleUserMention(currentMentionState) {
      this.showUserMentions = currentMentionState;
    },
    hideMentions() {
      this.showMentions = false;
    },
    toggleCannedMenu(value) {
      this.showCannedMenu = value;
    },
    toggleVariablesMenu(value) {
      this.showVariablesMenu = value;
    },
    openWhatsappTemplateModal() {
      this.showWhatsAppTemplatesModal = true;
    },
    hideWhatsappTemplatesModal() {
      this.showWhatsAppTemplatesModal = false;
    },
    openContentTemplateModal() {
      this.showContentTemplatesModal = true;
    },
    hideContentTemplatesModal() {
      this.showContentTemplatesModal = false;
    },
    confirmOnSendReply() {
      if (this.isReplyButtonDisabled) {
        return;
      }
      if (!this.showMentions) {
        const copilotAcceptedMessage = this.getCopilotAcceptedMessage();
        const isOnWhatsApp =
          this.isATwilioWhatsAppChannel ||
          this.isAWhatsAppCloudChannel ||
          this.is360DialogWhatsAppChannel;
        // Instagram and TikTok do not support sending text and attachments in the same message.
        // For Instagram, combining them causes duplicate messages due to separate echo events per component.
        // For TikTok, the API rejects messages that mix text and media.
        // To handle both cases, text and attachments are always sent as separate messages.
        const isOnInstagram = this.isAnInstagramChannel;
        const isOnTiktok = this.isATiktokChannel;
        if ((isOnWhatsApp || isOnInstagram || isOnTiktok) && !this.isPrivate) {
          this.sendMessageAsMultipleMessages(
            this.message,
            copilotAcceptedMessage
          );
        } else {
          const messagePayload = this.getMessagePayload(this.message);
          this.sendMessage(
            messagePayload,
            this.message,
            copilotAcceptedMessage
          );
        }

        if (!this.isPrivate) {
          this.clearEmailField();
        }

        this.clearMessage();
        this.hideEmojiPicker();
      }
    },
    sendMessageAsMultipleMessages(message, copilotAcceptedMessage = '') {
      const messages = this.getMultipleMessagesPayload(message);
      messages.forEach(messagePayload => {
        this.sendMessage(
          messagePayload,
          messagePayload.message || '',
          copilotAcceptedMessage
        );
      });
    },
    sendMessageAnalyticsData(
      isPrivate,
      { editorMessage = '', copilotAcceptedMessage = '' } = {}
    ) {
      const normalizeForComparison = message => {
        let normalizedMessage = message || '';

        if (this.sendWithSignature && this.messageSignature && !isPrivate) {
          const effectiveChannelType = getEffectiveChannelType(
            this.channelType,
            this.inbox?.medium || ''
          );
          normalizedMessage = removeSignature(
            normalizedMessage,
            this.messageSignature,
            effectiveChannelType
          );
        }

        return trimContent(normalizedMessage);
      };

      const normalizedAcceptedMessage = normalizeForComparison(
        copilotAcceptedMessage
      );
      const normalizedEditorMessage = normalizeForComparison(editorMessage);

      if (normalizedAcceptedMessage && normalizedEditorMessage) {
        useTrack(CAPTAIN_EVENTS.AI_ASSISTED_MESSAGE_SENT, {
          conversationId: this.conversationIdByRoute,
          channelType: this.channelType,
          editedBeforeSend:
            normalizedAcceptedMessage !== normalizedEditorMessage,
          isPrivate,
        });
      }

      // Analytics data for message signature is enabled or not in channels
      return isPrivate
        ? useTrack(CONVERSATION_EVENTS.SENT_PRIVATE_NOTE)
        : useTrack(CONVERSATION_EVENTS.SENT_MESSAGE, {
            channelType: this.channelType,
            signatureEnabled: this.sendWithSignature,
            hasReplyTo: !!this.inReplyTo?.id,
          });
    },
    async onSendReply() {
      const undefinedVariables = getUndefinedVariablesInMessage({
        message: this.message,
        variables: this.messageVariables,
      });
      if (undefinedVariables.length > 0) {
        const undefinedVariablesCount =
          undefinedVariables.length > 1 ? undefinedVariables.length : 1;
        this.undefinedVariableMessage = this.$t(
          'CONVERSATION.REPLYBOX.UNDEFINED_VARIABLES.MESSAGE',
          {
            undefinedVariablesCount,
            undefinedVariables: undefinedVariables.join(', '),
          }
        );

        const ok = await this.$refs.confirmDialog.showConfirmation();
        if (ok) {
          this.confirmOnSendReply();
        }
      } else {
        this.confirmOnSendReply();
      }
    },
    async sendMessage(
      messagePayload,
      editorMessage = '',
      copilotAcceptedMessage = ''
    ) {
      try {
        await this.$store.dispatch(
          'createPendingMessageAndSend',
          messagePayload
        );
        emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE);
        emitter.emit(BUS_EVENTS.MESSAGE_SENT);
        this.removeFromDraft();
        this.sendMessageAnalyticsData(messagePayload.private, {
          editorMessage,
          copilotAcceptedMessage,
        });
      } catch (error) {
        const errorMessage =
          error?.response?.data?.error || this.$t('CONVERSATION.MESSAGE_ERROR');
        useAlert(errorMessage);
      }
    },
    async onSendWhatsAppReply(messagePayload) {
      this.sendMessage({
        conversationId: this.currentChat.id,
        ...messagePayload,
      });
      this.hideWhatsappTemplatesModal();
    },
    async onSendContentTemplateReply(messagePayload) {
      this.sendMessage({
        conversationId: this.currentChat.id,
        ...messagePayload,
      });
      this.hideContentTemplatesModal();
    },
    replaceText(message, cannedMeta = {}) {
      // When called from the rich editor, message is null (text already inserted)
      // and only cannedMeta attachments need processing.
      const hasTextToReplace = message != null;

      // Direct mode: insert text into directMessage and queue photos
      if (this.isOnDirectMode) {
        if (hasTextToReplace) {
          const updatedMessage = replaceVariablesInMessage({
            message,
            variables: this.messageVariables,
          });
          this.directMessage = updatedMessage;
        }
        if (cannedMeta?.hasPhotos && cannedMeta?.photos?.length) {
          this.loadDirectCannedPhotos(cannedMeta.photos);
        }
        return;
      }

      if (hasTextToReplace) {
        if (this.sendWithSignature && !this.isPrivate) {
          const effectiveChannelType = getEffectiveChannelType(
            this.channelType,
            this.inbox?.medium || ''
          );
          message = appendSignature(
            message,
            this.messageSignature,
            effectiveChannelType
          );
        }

        const updatedMessage = replaceVariablesInMessage({
          message,
          variables: this.messageVariables,
        });

        setTimeout(() => {
          useTrack(CONVERSATION_EVENTS.INSERTED_A_CANNED_RESPONSE);
          this.message = updatedMessage;
        }, 100);
      }

      if (cannedMeta?.hasAudio && cannedMeta?.audioUrl) {
        this.attachCannedAudio(cannedMeta);
      }
      if (cannedMeta?.hasPhotos && cannedMeta?.photos?.length) {
        this.attachCannedPhotos(cannedMeta.photos);
      }
    },
    async attachCannedAudio({ audioUrl, audioFilename, audioContentType }) {
      try {
        const response = await fetch(audioUrl);
        if (!response.ok) throw new Error('audio fetch failed');
        const blob = await response.blob();
        const filename =
          audioFilename || `canned-audio-${Date.now()}.${
            (audioContentType || 'audio/mpeg').split('/')[1] || 'mp3'
          }`;
        const file = new File([blob], filename, {
          type: audioContentType || blob.type || 'audio/mpeg',
        });
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onloadend = () => {
          this.attachedFiles.push({
            currentChatId: this.currentChat.id,
            resource: { file },
            isPrivate: this.isPrivate,
            thumb: reader.result,
            blobSignedId: undefined,
            isRecordedAudio: true,
          });
        };
      } catch (error) {
        // Fail silently — text canned response was still inserted
        // eslint-disable-next-line no-console
        console.warn('[CannedAudio] failed to attach audio:', error);
      }
    },
    async attachCannedPhotos(photos) {
      for (const photo of photos) {
        try {
          const response = await fetch(photo.url);
          if (!response.ok) continue;
          const blob = await response.blob();
          const filename =
            photo.filename || `canned-photo-${Date.now()}.${
              (photo.content_type || 'image/jpeg').split('/')[1] || 'jpg'
            }`;
          const file = new File([blob], filename, {
            type: photo.content_type || blob.type || 'image/jpeg',
          });
          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.onloadend = () => {
            this.attachedFiles.push({
              currentChatId: this.currentChat.id,
              resource: { file },
              isPrivate: this.isPrivate,
              thumb: reader.result,
              blobSignedId: undefined,
            });
          };
        } catch (error) {
          // eslint-disable-next-line no-console
          console.warn('[CannedPhotos] failed to attach photo:', error);
        }
      }
    },
    async loadDirectCannedPhotos(photos) {
      this.directCannedPhotos = [];
      for (const photo of photos) {
        try {
          const response = await fetch(photo.url);
          if (!response.ok) continue;
          const blob = await response.blob();
          const filename =
            photo.filename || `photo-${Date.now()}.${
              (photo.content_type || 'image/jpeg').split('/')[1] || 'jpg'
            }`;
          const file = new File([blob], filename, {
            type: photo.content_type || blob.type || 'image/jpeg',
          });
          this.directCannedPhotos.push(file);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.warn('[DirectCannedPhotos] failed to load photo:', error);
        }
      }
    },
    setReplyMode(mode = REPLY_EDITOR_MODES.REPLY) {
      // Clear attachments when switching between private note and reply modes
      // This is to prevent from breaking the upload rules
      if (this.attachedFiles.length > 0) this.attachedFiles = [];

      const { can_reply: canReply } = this.currentChat;
      this.$store.dispatch('draftMessages/setReplyEditorMode', {
        mode,
      });
      if (canReply || this.isAWhatsAppChannel || this.isAPIInbox)
        this.replyType = mode;
      if (this.isRecordingAudio) {
        this.toggleAudioRecorder();
      }
    },
    clearEditorSelection() {
      this.updateEditorSelectionWith = '';
    },
    addIntoEditor(content) {
      if (this.showRichContentEditor) {
        this.updateEditorSelectionWith = content;
      } else {
        const textarea = this.$refs.messageInput?.$refs?.textarea;
        if (textarea) {
          const start = textarea.selectionStart;
          const end = textarea.selectionEnd;
          const before = this.message.substring(0, start);
          const after = this.message.substring(end);
          this.message = before + content + after;
          this.$nextTick(() => {
            const pos = start + content.length;
            textarea.focus();
            textarea.setSelectionRange(pos, pos);
          });
        } else {
          this.message += content;
        }
      }
      this.onFocus();
    },
    toggleRichContentEditor() {
      this.updateUISettings({
        display_rich_content_editor: !this.showRichContentEditor,
      });
      const plainTextSignature = extractTextFromMarkdown(this.messageSignature);
      if (!this.showRichContentEditor && this.messageSignature) {
        let message = removeSignature(this.message, this.messageSignature);
        message = extractTextFromMarkdown(message);
        message = appendSignature(message, plainTextSignature);
        this.message = message;
      } else {
        this.message = replaceSignature(
          this.message,
          plainTextSignature,
          this.messageSignature
        );
      }
    },
    executeCopilotAction(action, data) {
      this.copilot.execute(action, data);
    },
    clearMessage() {
      this.message = '';
      this.clearCopilotAcceptedMessage();
      if (this.sendWithSignature && !this.isPrivate) {
        // if signature is enabled, append it to the message
        const effectiveChannelType = getEffectiveChannelType(
          this.channelType,
          this.inbox?.medium || ''
        );
        this.message = appendSignature(
          this.message,
          this.messageSignature,
          effectiveChannelType
        );
      }
      this.attachedFiles = [];
      this.isRecordingAudio = false;
      this.resetReplyToMessage();
      this.resetAudioRecorderInput();
    },
    clearEmailField() {
      this.ccEmails = '';
      this.bccEmails = '';
      this.toEmails = '';
    },

    toggleEmojiPicker() {
      this.showEmojiPicker = !this.showEmojiPicker;
    },
    toggleAudioRecorder() {
      this.isRecordingAudio = !this.isRecordingAudio;
      if (!this.isRecordingAudio) {
        this.resetAudioRecorderInput();
      }
    },
    toggleAudioRecorderPlayPause() {
      if (!this.$refs.audioRecorderInput) return;
      if (!this.recordingAudioState) {
        this.$refs.audioRecorderInput.stopRecording();
      } else {
        this.$refs.audioRecorderInput.playPause();
      }
    },
    hideEmojiPicker() {
      if (this.showEmojiPicker) {
        this.toggleEmojiPicker();
      }
    },
    onTypingOn() {
      this.toggleTyping('on');
    },
    onTypingOff() {
      this.toggleTyping('off');
    },
    onBlur() {
      this.isFocused = false;
      this.saveDraft(this.conversationIdByRoute, this.replyType);
    },
    onFocus() {
      this.isFocused = true;
    },
    onRecordProgressChanged(duration) {
      this.recordingAudioDurationText = duration;
    },
    onFinishRecorder(file) {
      this.recordingAudioState = 'stopped';
      this.hasRecordedAudio = true;
      // Added a new key isRecordedAudio to the file to find it's and recorded audio
      // Because to filter and show only non recorded audio and other attachments
      const autoRecordedFile = {
        ...file,
        isRecordedAudio: true,
      };
      return file && this.onFileUpload(autoRecordedFile);
    },
    toggleTyping(status) {
      const conversationId = this.currentChat.id;
      const isPrivate = this.isPrivate;

      if (!conversationId) {
        return;
      }

      this.$store.dispatch('conversationTypingStatus/toggleTyping', {
        status,
        conversationId,
        isPrivate,
      });
    },
    attachFile({ blob, file }) {
      if (!this.showFileUpload && !this.isOnPrivateNote) return;

      const reader = new FileReader();
      reader.readAsDataURL(file.file);
      reader.onloadend = () => {
        this.attachedFiles.push({
          currentChatId: this.currentChat.id,
          resource: blob || file,
          isPrivate: this.isPrivate,
          thumb: reader.result,
          blobSignedId: blob ? blob.signed_id : undefined,
          isRecordedAudio: file?.isRecordedAudio || false,
        });
      };
    },
    removeAttachment(attachments) {
      this.attachedFiles = attachments;
    },
    setReplyToInPayload(payload) {
      if (this.inReplyTo?.id) {
        return {
          ...payload,
          contentAttributes: {
            ...payload.contentAttributes,
            in_reply_to: this.inReplyTo.id,
          },
        };
      }

      return payload;
    },
    getMultipleMessagesPayload(message) {
      const multipleMessagePayload = [];

      if (this.attachedFiles && this.attachedFiles.length) {
        let caption =
          this.isAnInstagramChannel || this.isATiktokChannel ? '' : message;
        this.attachedFiles.forEach(attachment => {
          const attachedFile = this.globalConfig.directUploadsEnabled
            ? attachment.blobSignedId
            : attachment.resource.file;
          let attachmentPayload = {
            conversationId: this.currentChat.id,
            files: [attachedFile],
            private: false,
            message: caption,
            sender: this.sender,
          };

          attachmentPayload = this.setReplyToInPayload(attachmentPayload);
          multipleMessagePayload.push(attachmentPayload);
          // For WhatsApp, only the first attachment gets a caption
          if (!this.isAnInstagramChannel) caption = '';
        });
      }

      const hasNoAttachments =
        !this.attachedFiles || !this.attachedFiles.length;
      // For Instagram and TikTok, text must always be sent as a separate message (no captions on attachments).
      // For WhatsApp, we only need a text message if there are no attachments.
      if (
        ((this.isAnInstagramChannel || this.isATiktokChannel) &&
          this.message) ||
        (!(this.isAnInstagramChannel || this.isATiktokChannel) &&
          hasNoAttachments)
      ) {
        let messagePayload = {
          conversationId: this.currentChat.id,
          message,
          private: false,
          sender: this.sender,
        };

        messagePayload = this.setReplyToInPayload(messagePayload);

        multipleMessagePayload.push(messagePayload);
      }

      return multipleMessagePayload;
    },
    getMessagePayload(message) {
      const messageWithQuote = this.getMessageWithQuotedEmailText(message);

      let messagePayload = {
        conversationId: this.currentChat.id,
        message: messageWithQuote,
        private: this.isPrivate,
        sender: this.sender,
      };
      messagePayload = this.setReplyToInPayload(messagePayload);

      if (this.attachedFiles && this.attachedFiles.length) {
        messagePayload.files = [];
        this.attachedFiles.forEach(attachment => {
          if (this.globalConfig.directUploadsEnabled) {
            messagePayload.files.push(attachment.blobSignedId);
          } else {
            messagePayload.files.push(attachment.resource.file);
          }
        });
      }

      if (this.ccEmails && !this.isOnPrivateNote) {
        messagePayload.ccEmails = this.ccEmails;
      }

      if (this.bccEmails && !this.isOnPrivateNote) {
        messagePayload.bccEmails = this.bccEmails;
      }

      if (this.toEmails && !this.isOnPrivateNote) {
        messagePayload.toEmails = this.toEmails;
      }
      return messagePayload;
    },
    setCcEmails(value) {
      this.bccEmails = value.bccEmails;
      this.ccEmails = value.ccEmails;
    },
    setCCAndToEmailsFromLastChat() {
      const conversationContact = this.currentChat?.meta?.sender?.email || '';
      const { email: inboxEmail, forward_to_email: forwardToEmail } =
        this.inbox;

      const { cc, bcc, to } = getRecipients(
        this.lastEmail,
        conversationContact,
        inboxEmail,
        forwardToEmail
      );

      this.toEmails = to.join(', ');
      this.ccEmails = cc.join(', ');
      this.bccEmails = bcc.join(', ');
    },
    fetchAndSetReplyTo() {
      const replyStorageKey = LOCAL_STORAGE_KEYS.MESSAGE_REPLY_TO;
      const replyToMessageId = LocalStorage.getFromJsonStore(
        replyStorageKey,
        this.conversationId
      );

      this.inReplyTo = this.currentChat?.messages?.find(message => {
        if (message.id === replyToMessageId) {
          return true;
        }
        return false;
      });
    },
    onReplyToMessage() {
      this.fetchAndSetReplyTo();
      if (this.inReplyTo) {
        this.$nextTick(() => {
          const pos = this.isSignatureEnabledForInbox ? 'start' : 'end';
          this.messageEditor?.focusEditorInputField(pos);
        });
      }
    },
    resetReplyToMessage() {
      const replyStorageKey = LOCAL_STORAGE_KEYS.MESSAGE_REPLY_TO;
      LocalStorage.deleteFromJsonStore(replyStorageKey, this.conversationId);
      emitter.emit(BUS_EVENTS.TOGGLE_REPLY_TO_MESSAGE);
    },
    onNewConversationModalActive(isActive) {
      // Issue is if the new conversation modal is open and we drag and drop the file
      // then the file is not getting attached to the new conversation modal
      // and it is getting attached to the current conversation reply box
      // so to fix this we are removing the drag and drop event listener from the current conversation reply box
      // When new conversation modal is open
      this.newConversationModalActive = isActive;
    },
    onSearchPopoverClose() {
      this.showArticleSearchPopover = false;
    },
    toggleInsertArticle() {
      this.showArticleSearchPopover = !this.showArticleSearchPopover;
    },
    resetAudioRecorderInput() {
      this.recordingAudioDurationText = '00:00';
      this.isRecordingAudio = false;
      this.recordingAudioState = '';
      this.hasRecordedAudio = false;
      // Only clear the recorded audio when we click toggle button.
      this.attachedFiles = this.attachedFiles.filter(
        file => !file?.isRecordedAudio
      );
    },
    toggleEditorSize() {
      this.$emit('toggleEditorSize');
      this.$nextTick(() => this.messageEditor?.focusEditorInputField());
    },
    togglePixTextModal() {
      this.showPixTextModal = !this.showPixTextModal;
    },
    savePixStandardText(text) {
      this.pixStandardText = text;
      this.showPixTextModal = false;
      // Save per account
      const storageKey = `${LOCAL_STORAGE_KEYS.PIX_STANDARD_TEXT}_${this.accountId}`;
      LocalStorage.set(storageKey, text);
    },
    loadPixStandardText() {
      // Load per account
      const storageKey = `${LOCAL_STORAGE_KEYS.PIX_STANDARD_TEXT}_${this.accountId}`;
      const savedText = LocalStorage.get(storageKey);
      if (savedText) {
        this.pixStandardText = savedText;
      }
    },
    async sendPixCode() {
      if (!this.pixCode.trim()) {
        useAlert(this.$t('CONVERSATION.REPLYBOX.PIX_CODE_PLACEHOLDER'));
        return;
      }

      try {
        // First message: Standard text (with PIX flag)
        const textMessagePayload = {
          conversationId: this.currentChat.id,
          message: this.pixStandardText,
          private: false,
          contentAttributes: {
            is_pix: true,
            pix_message_type: 'instruction',
          },
        };
        await this.$store.dispatch(
          'createPendingMessageAndSend',
          textMessagePayload
        );

        // Second message: Only the PIX code (with PIX flag)
        const pixCodePayload = {
          conversationId: this.currentChat.id,
          message: this.pixCode,
          private: false,
          contentAttributes: {
            is_pix: true,
            pix_message_type: 'code',
            pix_code: this.pixCode,
          },
        };
        await this.$store.dispatch(
          'createPendingMessageAndSend',
          pixCodePayload
        );

        emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE);
        emitter.emit(BUS_EVENTS.MESSAGE_SENT);
        this.pixCode = '';
        useAlert(this.$t('CONVERSATION.REPLYBOX.PIX_SEND') + ' ✓');
      } catch (error) {
        const errorMessage =
          error?.response?.data?.error || this.$t('CONVERSATION.MESSAGE_ERROR');
        useAlert(errorMessage);
      }
    },
    async onQuepasaReconnected() {
      this.quepasaConnected = true;
      if (this.pendingDirectResend && this.directMessage.trim()) {
        this.pendingDirectResend = false;
        await new Promise(r => setTimeout(r, 3000));
        await this.sendDirectMessage();
      }
    },
    connectQuepasaDirect() {
      emitter.emit(BUS_EVENTS.QUEPASA_RECONNECT);
    },
    onDirectFileSelect(event) {
      const file = event.target.files[0];
      if (!file) return;
      if (file.size > 50 * 1024 * 1024) {
        useAlert(this.$t('CONVERSATION.REPLYBOX.DIRECT_FILE_TOO_LARGE'));
        return;
      }
      this.directFile = file;
      event.target.value = '';
    },
    removeDirectFile() {
      this.directFile = null;
    },
    directFileIcon() {
      if (!this.directFile) return 'i-lucide-file';
      const type = this.directFile.type;
      if (type.startsWith('image/')) return 'i-lucide-image';
      if (type.startsWith('video/')) return 'i-lucide-video';
      if (type.startsWith('audio/')) return 'i-lucide-music';
      if (type === 'application/pdf') return 'i-lucide-file-text';
      return 'i-lucide-file';
    },
    formatDirectFileSize(bytes) {
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    },
    startDirectAudioRecording() {
      this.directRecordingAudio = true;
      this.directAudioState = 'recording';
    },
    stopDirectAudioRecording() {
      if (this.$refs.directAudioRecorder) {
        this.$refs.directAudioRecorder.stopRecording();
      }
    },
    cancelDirectAudioRecording() {
      this.directRecordingAudio = false;
      this.directAudioState = 'idle';
      this.directAudioDuration = '';
    },
    onDirectAudioProgress(duration) {
      this.directAudioDuration = duration;
    },
    onDirectAudioFinish(audioData) {
      if (audioData && audioData.file) {
        this.directFile = audioData.file;
      }
      this.directRecordingAudio = false;
      this.directAudioState = 'idle';
      this.directAudioDuration = '';
    },
    async checkQuepasaConnection() {
      if (!this.inbox.quepasa_token) {
        this.quepasaConnected = false;
        return false;
      }
      try {
        const response = await QuepasaAPI.getStatus(this.inboxId);
        this.quepasaConnected = !!response.data.connected;
      } catch {
        this.quepasaConnected = false;
      }
      return this.quepasaConnected;
    },
    recordDirectSend() {
      const key = `direct_${this.conversationId}`;
      if (!this.directRateLimit[key]) {
        this.directRateLimit[key] = [];
      }
      const now = Date.now();
      const oneHour = 60 * 60 * 1000;
      this.directRateLimit[key] = this.directRateLimit[key].filter(
        ts => now - ts < oneHour
      );
      this.directRateLimit[key].push(now);
    },
    async sendDirectMessage() {
      const hasPhotos = this.directCannedPhotos.length > 0;
      if (!this.directMessage.trim() && !this.directFile && !hasPhotos) return;
      if (this.directSending) return;

      if (!this.contactPhoneForQuepasa) {
        useAlert(this.$t('CONVERSATION.REPLYBOX.DIRECT_NO_PHONE'));
        return;
      }

      this.directSending = true;
      try {
        const connected = await this.checkQuepasaConnection();
        if (!connected) {
          const wantsReconnect = window.confirm(
            this.$t('CONVERSATION.REPLYBOX.DIRECT_DISCONNECTED_CONFIRM')
          );
          if (wantsReconnect) {
            this.pendingDirectResend = true;
            emitter.emit(BUS_EVENTS.QUEPASA_RECONNECT);
          }
          return;
        }

        // Send text message (with optional single file)
        if (this.directMessage.trim() || this.directFile) {
          const response = await QuepasaAPI.directSend(this.inboxId, {
            phone: this.contactPhoneForQuepasa,
            text: this.directMessage,
            conversationId: this.conversationId,
            file: this.directFile,
          });
          const messageData = response.data?.message_data;
          if (messageData) {
            this.$store.commit('ADD_MESSAGE', messageData);
          }
        }

        // Send canned response photos as separate messages
        for (const photoFile of this.directCannedPhotos) {
          const photoResponse = await QuepasaAPI.directSend(this.inboxId, {
            phone: this.contactPhoneForQuepasa,
            text: '',
            conversationId: this.conversationId,
            file: photoFile,
          });
          const photoMsgData = photoResponse.data?.message_data;
          if (photoMsgData) {
            this.$store.commit('ADD_MESSAGE', photoMsgData);
          }
        }

        this.pendingDirectResend = false;
        this.directMessage = '';
        this.directFile = null;
        this.directCannedPhotos = [];

        emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE);

        useAlert(this.$t('CONVERSATION.REPLYBOX.DIRECT_SENT'));
      } catch (error) {
        const status = error?.response?.status;
        if (status === 502 || status === 503) {
          this.quepasaConnected = false;
          const wantsReconnect = window.confirm(
            this.$t('CONVERSATION.REPLYBOX.DIRECT_DISCONNECTED_CONFIRM')
          );
          if (wantsReconnect) {
            this.pendingDirectResend = true;
            emitter.emit(BUS_EVENTS.QUEPASA_RECONNECT);
          }
        } else {
          const msg =
            error?.response?.data?.error ||
            this.$t('CONVERSATION.REPLYBOX.DIRECT_ERROR');
          useAlert(msg);
        }
      } finally {
        this.directSending = false;
      }
    },
    onSubmitCopilotReply() {
      const acceptedMessage = this.copilot.accept();
      this.message = acceptedMessage;
      this.setCopilotAcceptedMessage(acceptedMessage);
    },
  },
};
</script>

<template>
  <ReplyBoxBanner :message="message" :is-on-private-note="isOnPrivateNote" />
  <div ref="replyEditor" class="reply-box" :class="replyBoxClass">
    <ReplyTopPanel
      :mode="replyType"
      :conversation-id="conversationId"
      :is-reply-restricted="isReplyRestricted"
      :disabled="
        (copilot.isActive.value && copilot.isButtonDisabled.value) ||
        showAudioRecorderEditor
      "
      :is-editor-disabled="isEditorDisabled"
      :show-direct-send="showDirectSendTab"
      :is-message-length-reaching-threshold="isMessageLengthReachingThreshold"
      :characters-remaining="charactersRemaining"
      :editor-content="message"
      @set-reply-mode="setReplyMode"
      @toggle-editor-size="toggleEditorSize"
      @toggle-copilot="copilot.toggleEditor"
      @execute-copilot-action="executeCopilotAction"
    />
    <ArticleSearchPopover
      v-if="showArticleSearchPopover && connectedPortalSlug"
      :selected-portal-slug="connectedPortalSlug"
      @insert="handleInsert"
      @close="onSearchPopoverClose"
    />
    <Transition
      mode="out-in"
      enter-active-class="transition-all duration-300 ease-out"
      enter-from-class="opacity-0 translate-y-2 scale-[0.98]"
      enter-to-class="opacity-100 translate-y-0 scale-100"
      leave-active-class="transition-all duration-200 ease-in"
      leave-from-class="opacity-100 translate-y-0 scale-100"
      leave-to-class="opacity-0 translate-y-2 scale-[0.98]"
    >
      <div :key="copilot.editorTransitionKey.value" class="reply-box__top">
        <ReplyToMessage
          v-if="shouldShowReplyToMessage"
          :message="inReplyTo"
          @dismiss="resetReplyToMessage"
        />
        <CannedResponse
          v-if="showMentions && hasSlashCommand"
          v-on-clickaway="hideMentions"
          class="normal-editor__canned-box"
          :search-key="mentionSearchKey"
          @replace="replaceText"
        />
        <EmojiInput
          v-if="showEmojiPicker"
          v-on-clickaway="hideEmojiPicker"
          :class="{
            'emoji-dialog--expanded': isOnExpandedLayout,
          }"
          :on-click="addIntoEditor"
        />
        <ReplyEmailHead
          v-if="showReplyHead && isDefaultEditorMode"
          v-model:cc-emails="ccEmails"
          v-model:bcc-emails="bccEmails"
          v-model:to-emails="toEmails"
        />
        <AudioRecorder
          v-if="showAudioRecorderEditor"
          ref="audioRecorderInput"
          :audio-record-format="audioRecordFormat"
          @recorder-progress-changed="onRecordProgressChanged"
          @finish-record="onFinishRecorder"
          @play="recordingAudioState = 'playing'"
          @pause="recordingAudioState = 'paused'"
        />
        <CopilotEditorSection
          v-if="
            copilot.isActive.value && !showAudioRecorderEditor && !isOnPixMode && !isOnDirectMode
          "
          :show-copilot-editor="copilot.showEditor.value"
          :is-generating-content="copilot.isGenerating.value"
          :generated-content="copilot.generatedContent.value"
          :placeholder="$t('CONVERSATION.FOOTER.COPILOT_MSG_INPUT')"
          @focus="onFocus"
          @blur="onBlur"
          @clear-selection="clearEditorSelection"
          @close="copilot.showEditor.value = false"
          @content-ready="copilot.setContentReady"
          @send="copilot.sendFollowUp"
        />
        <div
          v-else-if="
            !showRichContentEditor && !showAudioRecorderEditor && !isOnPixMode && !isOnDirectMode
          "
          class="textarea-wrapper"
        >
          <SpellCheckerSuggestions
            :text="message"
            :textarea-ref="$refs.messageInput"
            @apply-correction="message = $event"
          />
          <ResizableTextArea
            ref="messageInput"
            v-model="message"
            class="rounded-none input"
            :placeholder="messagePlaceHolder"
            :min-height="4"
            :signature="signatureToApply"
            allow-signature
            :send-with-signature="sendWithSignature"
            @typing-off="onTypingOff"
            @typing-on="onTypingOn"
            @focus="onFocus"
            @blur="onBlur"
          />
        </div>
        <WootMessageEditor
          v-else-if="!showAudioRecorderEditor && !isOnPixMode && !isOnDirectMode"
          ref="messageEditor"
          v-model="message"
          :conversation-id="conversationId"
          :editor-id="editorStateId"
          class="input"
          :is-private="isOnPrivateNote"
          :placeholder="messagePlaceHolder"
          :update-selection-with="updateEditorSelectionWith"
          :min-height="4"
          :disabled="isEditorDisabled"
          enable-variables
          :variables="messageVariables"
          :signature="signatureToApply"
          allow-signature
          :channel-type="channelType"
          @typing-off="onTypingOff"
          @typing-on="onTypingOn"
          @focus="onFocus"
          @blur="onBlur"
          @toggle-user-mention="toggleUserMention"
          @toggle-canned-menu="toggleCannedMenu"
          @toggle-variables-menu="toggleVariablesMenu"
          @clear-selection="clearEditorSelection"
          @execute-copilot-action="executeCopilotAction"
          @replace-text="replaceText"
        />
        <QuotedEmailPreview
          v-if="shouldShowQuotedPreview && isDefaultEditorMode"
          :quoted-email-text="quotedEmailText"
          :preview-text="quotedEmailPreviewText"
          class="mb-2"
          @toggle="toggleQuotedReply"
        />
        <!-- Direct Send via Quepasa Section -->
        <div v-if="isOnDirectMode" class="p-4">
          <div v-if="!hasQuepasaConfig" class="flex flex-col items-center gap-3 py-4 text-center">
            <span class="i-lucide-wifi-off size-8 text-red-400" />
            <span class="text-sm font-medium text-red-600 dark:text-red-400">
              {{ $t('CONVERSATION.REPLYBOX.DIRECT_DISCONNECTED') }}
            </span>
            <button
              class="flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-green-700"
              @click="connectQuepasaDirect"
            >
              <span class="i-lucide-qr-code size-4" />
              {{ $t('CONVERSATION.REPLYBOX.DIRECT_CONNECT_BTN') }}
            </button>
          </div>
          <div v-else class="flex flex-col gap-3">
            <div class="flex items-center">
              <span class="text-xs font-medium text-green-600 dark:text-green-400">
                {{ $t('CONVERSATION.REPLYBOX.DIRECT_VIA_QUEPASA') }}
              </span>
            </div>
            <div
              v-if="quepasaConnected === false"
              class="text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 p-2 rounded"
            >
              {{ $t('CONVERSATION.REPLYBOX.DIRECT_DISCONNECTED') }}
            </div>
            <!-- Audio Recorder -->
            <div v-if="directRecordingAudio">
              <div class="mb-2 rounded-lg border border-green-300 bg-green-50 p-2 dark:border-green-700 dark:bg-green-950">
                <div class="flex items-center gap-2 mb-1">
                  <span class="i-lucide-mic size-3.5 text-red-500 animate-pulse" />
                  <span class="text-xs font-medium text-red-600 dark:text-red-400">
                    {{ directAudioDuration || '00:00' }}
                  </span>
                </div>
                <AudioRecorder
                  ref="directAudioRecorder"
                  :audio-record-format="audioRecordFormat"
                  @recorder-progress-changed="onDirectAudioProgress"
                  @finish-record="onDirectAudioFinish"
                  @play="directAudioState = 'playing'"
                  @pause="directAudioState = 'paused'"
                />
                <div class="mt-2 flex items-center gap-2">
                  <button
                    class="flex items-center gap-1 rounded-lg bg-red-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-600"
                    @click="stopDirectAudioRecording"
                  >
                    <span class="i-lucide-square size-3" />
                    {{ $t('CONVERSATION.REPLYBOX.DIRECT_STOP_AUDIO') }}
                  </button>
                  <button
                    class="flex items-center gap-1 rounded-lg border border-slate-300 px-3 py-1.5 text-xs text-slate-600 hover:bg-slate-100 dark:border-slate-600 dark:text-slate-400 dark:hover:bg-slate-800"
                    @click="cancelDirectAudioRecording"
                  >
                    <span class="i-lucide-x size-3" />
                    {{ $t('CONVERSATION.REPLYBOX.DIRECT_CANCEL_AUDIO') }}
                  </button>
                </div>
              </div>
            </div>
            <!-- File Preview -->
            <div
              v-if="directFile"
              class="flex items-center gap-2 rounded-lg border border-green-300 bg-green-50 px-3 py-2 dark:border-green-700 dark:bg-green-950"
            >
              <span :class="directFileIcon()" class="size-4 text-green-600" />
              <span class="flex-1 truncate text-xs text-green-800 dark:text-green-200">
                {{ directFile.name }}
              </span>
              <span class="text-[10px] text-green-600 dark:text-green-400">
                {{ formatDirectFileSize(directFile.size) }}
              </span>
              <button
                class="rounded p-0.5 text-green-600 hover:bg-green-200 dark:hover:bg-green-800"
                @click="removeDirectFile"
              >
                <span class="i-lucide-x size-3.5" />
              </button>
            </div>
            <!-- Canned Photos Preview -->
            <div
              v-if="directCannedPhotos.length"
              class="flex flex-wrap gap-2 rounded-lg border border-green-300 bg-green-50 p-2 dark:border-green-700 dark:bg-green-950"
            >
              <span class="w-full text-[10px] font-medium text-green-600 dark:text-green-400">
                {{ directCannedPhotos.length }} foto(s) da resposta pronta
              </span>
              <button
                class="text-[10px] text-red-500 hover:text-red-700"
                @click="directCannedPhotos = []"
              >
                Remover fotos
              </button>
            </div>
            <textarea
              v-if="!directRecordingAudio"
              v-model="directMessage"
              class="w-full p-3 border rounded-lg resize-none focus:outline-none"
              style="background-color: #ecfdf5; border-color: #6ee7b7; color: #1a1a1a"
              :placeholder="$t('CONVERSATION.REPLYBOX.DIRECT_PLACEHOLDER')"
              :disabled="directSending"
              rows="3"
            />
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-2">
                <span class="text-xs text-n-slate-10">
                  {{ contactPhoneForQuepasa || $t('CONVERSATION.REPLYBOX.DIRECT_NO_PHONE') }}
                </span>
                <label
                  v-if="!directRecordingAudio"
                  class="flex cursor-pointer items-center gap-1 rounded-lg border border-green-300 px-2 py-1 text-xs text-green-700 transition-colors hover:bg-green-50 dark:border-green-700 dark:text-green-400 dark:hover:bg-green-950"
                  :class="{ 'pointer-events-none opacity-50': directSending }"
                >
                  <span class="i-lucide-paperclip size-3.5" />
                  {{ $t('CONVERSATION.REPLYBOX.DIRECT_ATTACH') }}
                  <input
                    type="file"
                    class="hidden"
                    accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip,.rar"
                    :disabled="directSending"
                    @change="onDirectFileSelect"
                  />
                </label>
                <button
                  v-if="!directRecordingAudio"
                  class="flex items-center gap-1 rounded-lg border border-green-300 px-2 py-1 text-xs text-green-700 transition-colors hover:bg-green-50 dark:border-green-700 dark:text-green-400 dark:hover:bg-green-950"
                  :class="{ 'pointer-events-none opacity-50': directSending }"
                  :disabled="directSending"
                  @click="startDirectAudioRecording"
                >
                  <span class="i-lucide-mic size-3.5" />
                  {{ $t('CONVERSATION.REPLYBOX.DIRECT_RECORD_AUDIO') }}
                </button>
              </div>
              <button
                v-if="!directRecordingAudio"
                class="px-4 py-2 text-sm font-medium text-white transition-colors rounded-lg disabled:opacity-50 disabled:cursor-not-allowed bg-green-600 hover:bg-green-700"
                :disabled="(!directMessage.trim() && !directFile) || directSending || !contactPhoneForQuepasa"
                @click="sendDirectMessage"
              >
                <span v-if="directSending">{{ $t('CONVERSATION.REPLYBOX.DIRECT_SENDING') }}</span>
                <span v-else>{{ $t('CONVERSATION.REPLYBOX.DIRECT_SEND_BTN') }}</span>
              </button>
            </div>
          </div>
        </div>
        <!-- PIX Editor Section (legacy, hidden) -->
        <div v-if="isOnPixMode" class="pix-editor-section p-4">
          <div class="flex flex-col gap-3">
            <textarea
              v-model="pixCode"
              class="w-full p-3 border rounded-lg resize-none text-n-slate-12 focus:outline-none"
              style="background-color: #e0f2fe; border-color: #7dd3fc"
              :placeholder="$t('CONVERSATION.REPLYBOX.PIX_CODE_PLACEHOLDER')"
              rows="3"
            />
            <div class="flex items-center justify-between gap-2">
              <button
                class="px-4 py-2 text-sm font-medium transition-colors rounded-lg bg-n-alpha-2 text-n-slate-11 hover:bg-n-alpha-3"
                @click="togglePixTextModal"
              >
                {{ $t('CONVERSATION.REPLYBOX.PIX_STANDARD_TEXT_BUTTON') }}
              </button>
              <button
                class="px-4 py-2 text-sm font-medium text-white transition-colors rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                style="background-color: #38bdf8"
                :disabled="!pixCode.trim()"
                @click="sendPixCode"
              >
                {{ $t('CONVERSATION.REPLYBOX.PIX_SEND') }}
              </button>
            </div>
          </div>
        </div>
        <div
          v-if="hasAttachments && isDefaultEditorMode"
          class="bg-transparent py-0 mb-2"
          @paste="onPaste"
        >
          <AttachmentPreview
            class="mt-2"
            :attachments="attachedFiles"
            @remove-attachment="removeAttachment"
          />
        </div>
        <MessageSignatureMissingAlert
          v-if="
            isSignatureEnabledForInbox &&
            !isSignatureAvailable &&
            isDefaultEditorMode
          "
          class="mb-2"
        />
      </div>
    </Transition>

    <Transition
      mode="out-in"
      enter-active-class="transition-all duration-300 ease-out"
      enter-from-class="opacity-0 translate-y-2 scale-[0.98]"
      enter-to-class="opacity-100 translate-y-0 scale-100"
      leave-active-class="transition-all duration-200 ease-in"
      leave-from-class="opacity-100 translate-y-0 scale-100"
      leave-to-class="opacity-0 translate-y-2 scale-[0.98]"
    >
      <CopilotReplyBottomPanel
        v-if="copilot.isActive.value"
        key="copilot-bottom-panel"
        :is-generating-content="copilot.isButtonDisabled.value"
        @submit="onSubmitCopilotReply"
        @cancel="copilot.reset"
      />
    </Transition>
    <!-- PIX Standard Text Modal -->
    <div
      v-if="showPixTextModal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      @click.self="togglePixTextModal"
    >
      <div
        class="w-full max-w-lg p-6 mx-4 bg-white rounded-xl dark:bg-n-solid-3 shadow-xl"
      >
        <h3 class="mb-4 text-lg font-semibold text-n-slate-12">
          {{ $t('CONVERSATION.REPLYBOX.PIX_STANDARD_TEXT_MODAL_TITLE') }}
        </h3>
        <textarea
          ref="pixStandardTextInput"
          v-model="pixStandardText"
          class="w-full p-3 border rounded-lg resize-y bg-n-solid-2 border-n-weak text-n-slate-12 focus:border-n-brand focus:outline-none min-h-[200px] max-h-[400px]"
          rows="8"
        />
        <div class="flex justify-end gap-2 mt-4">
          <button
            class="px-4 py-2 text-sm font-medium transition-colors rounded-lg bg-n-alpha-2 text-n-slate-11 hover:bg-n-alpha-3"
            @click="togglePixTextModal"
          >
            {{ $t('CONVERSATION.REPLYBOX.PIX_CANCEL') }}
          </button>
          <button
            class="px-4 py-2 text-sm font-medium text-white transition-colors rounded-lg bg-n-brand hover:bg-n-brand-dark"
            @click="savePixStandardText(pixStandardText)"
          >
            {{ $t('CONVERSATION.REPLYBOX.PIX_STANDARD_TEXT_SAVE') }}
          </button>
        </div>
      </div>
    </div>
    <ReplyBottomPanel
      v-if="!isOnPixMode && !isOnDirectMode"
      :conversation-id="conversationId"
      :enable-multiple-file-upload="enableMultipleFileUpload"
      :enable-whats-app-templates="showWhatsappTemplates"
      :enable-content-templates="showContentTemplates"
      :inbox="inbox"
      :is-on-private-note="isOnPrivateNote"
      :is-recording-audio="isRecordingAudio"
      :is-send-disabled="isReplyButtonDisabled"
      :is-note="isPrivate"
      :is-editor-disabled="isEditorDisabled"
      :on-file-upload="onFileUpload"
      :on-send="onSendReply"
      :conversation-type="conversationType"
      :recording-audio-duration-text="recordingAudioDurationText"
      :recording-audio-state="recordingAudioState"
      :send-button-text="replyButtonLabel"
      :show-audio-recorder="showAudioRecorder"
      :show-editor-toggle="isAPIInbox && !isOnPrivateNote"
      :show-emoji-picker="showEmojiPicker"
      :show-file-upload="showFileUpload"
      :show-quoted-reply-toggle="shouldShowQuotedReplyToggle"
      :quoted-reply-enabled="quotedReplyPreference"
      :toggle-audio-recorder-play-pause="toggleAudioRecorderPlayPause"
      :toggle-audio-recorder="toggleAudioRecorder"
      :toggle-emoji-picker="toggleEmojiPicker"
      :message="message"
      :portal-slug="connectedPortalSlug"
      :new-conversation-modal-active="newConversationModalActive"
      @select-whatsapp-template="openWhatsappTemplateModal"
      @select-content-template="openContentTemplateModal"
      @toggle-editor="toggleRichContentEditor"
      @replace-text="replaceText"
      @toggle-insert-article="toggleInsertArticle"
      @toggle-quoted-reply="toggleQuotedReply"
      @open-brasil-api="showBrasilApiModal = true"
    />
    <WhatsappTemplates
      :inbox-id="inbox.id"
      :show="showWhatsAppTemplatesModal"
      @close="hideWhatsappTemplatesModal"
      @on-send="onSendWhatsAppReply"
      @cancel="hideWhatsappTemplatesModal"
    />

    <ContentTemplates
      :inbox-id="inbox.id"
      :show="showContentTemplatesModal"
      @close="hideContentTemplatesModal"
      @on-send="onSendContentTemplateReply"
      @cancel="hideContentTemplatesModal"
    />

    <BrasilApiModal
      v-model:show="showBrasilApiModal"
      @close="showBrasilApiModal = false"
    />

    <woot-confirm-modal
      ref="confirmDialog"
      :title="$t('CONVERSATION.REPLYBOX.UNDEFINED_VARIABLES.TITLE')"
      :description="undefinedVariableMessage"
    />
  </div>
</template>

<style lang="scss" scoped>
.send-button {
  @apply mb-0;
}

.reply-box {
  @apply relative mb-2 mx-2 border border-n-weak rounded-xl bg-n-solid-1;

  &.is-private {
    @apply bg-n-solid-amber dark:border-n-amber-3/10 border-n-amber-12/5;
  }
}

.send-button {
  @apply mb-0;
}

.reply-box__top {
  @apply relative py-0 px-3 -mt-px;
}

.reply-box__top .input {
  max-height: 40vh;
  overflow-y: visible;
}

.reply-box__top .textarea-wrapper {
  max-height: 40vh;
  overflow-y: auto;
}

.textarea-wrapper {
  @apply relative w-full;
}

.emoji-dialog {
  @apply top-[unset] -bottom-10 ltr:-left-80 ltr:right-[unset] rtl:left-[unset] rtl:-right-80;

  &::before {
    filter: drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.08));
    @apply ltr:-right-4 bottom-2 rtl:-left-4 ltr:rotate-[270deg] rtl:rotate-[90deg];
  }
}

.emoji-dialog--expanded {
  @apply left-[unset] bottom-0 absolute z-[100];

  &::before {
    transform: rotate(0deg);
    @apply ltr:left-1 rtl:right-1 -bottom-2;
  }
}
</style>
